import React, { useEffect, useState } from 'react';
import { Search, ArrowRight, FileImage, ExternalLink } from 'lucide-react';
import api from '../lib/adminApi';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  BillRow,
  Page,
  Period,
  Review,
  amount,
  dateTime,
  endpoint,
  message,
  useReport,
  humanize,
} from './api';
import { Empty, LoadState, Notice, Pagination, Status } from './shared';
export default function Receipts({
  period,
  filter,
  setFilter,
  refresh,
  retry,
  onBill,
  onUser,
}: {
  period: Period;
  filter: string;
  setFilter: (s: string) => void;
  refresh: number;
  retry: () => void;
  onBill: (id: string) => void;
  onUser: (id: string) => void;
}) {
  const [q, setQ] = useState(''),
    [search, setSearch] = useState(''),
    [offset, setOffset] = useState(0);
  useEffect(() => {
    setOffset(0);
  }, [filter, period]);
  const { data, loading, error } = useReport<Page<BillRow>>(
    '/receipts',
    {
      ...period,
      status: filter,
      q: search,
      offset,
    },
    refresh
  );
  return (
    <section className="admin-panel">
      <div className="admin-section-heading">
        <div>
          <h2>{filter === 'attention' ? 'The attention queue' : 'Receipt processing'}</h2>
          <p>
            {filter === 'attention'
              ? 'Unresolved failures and bills processing for over 15 minutes, across all dates.'
              : 'Inspect the receipt, compare its extraction, and record a quality review.'}
          </p>
        </div>
      </div>
      <div className="admin-filters">
        <form
          className="admin-search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(q);
            setOffset(0);
          }}
        >
          <Search size={18} />
          <input
            aria-label="Search receipts"
            placeholder="Search by bill, person, or ID…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button variant="ghost" type="submit">
            Search
          </Button>
        </form>
        <select
          aria-label="Receipt status"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="attention">Needs attention · all dates</option>
          <option value="all">All in date range</option>
          <option value="unreviewed">Unreviewed</option>
          <option value="failed">Failed</option>
          <option value="stalled">Delayed</option>
          <option value="processing">Processing</option>
          <option value="success">Successful</option>
        </select>
      </div>
      <LoadState loading={loading} error={error} retry={retry} />
      {data && (
        <>
          {data.warnings?.map((w) => (
            <Notice key={w}>{w}</Notice>
          ))}
          {data.items.length ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Receipt bill</th>
                    <th>Created by</th>
                    <th>Processing</th>
                    <th>Review</th>
                    <th>
                      <span className="sr-only">Inspect</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((b) => (
                    <tr key={b.id}>
                      <td>
                        <button className="admin-text-link" onClick={() => onBill(b.id)}>
                          {b.title}
                        </button>
                        <small>
                          {dateTime(b.createdAt)}
                          {b.isTestData && ' · Test'}
                        </small>
                        {b.processingError && (
                          <p className="admin-row-error">{b.processingError}</p>
                        )}
                      </td>
                      <td>
                        <button
                          className="admin-text-link secondary"
                          onClick={() => onUser(b.userId)}
                        >
                          {b.creator}
                        </button>
                      </td>
                      <td>
                        <Status value={b.processingStatus} />
                      </td>
                      <td>
                        {b.review ? (
                          <Status value={b.review.outcome} />
                        ) : (
                          <span className="admin-muted">
                            {b.auditId ? 'Not reviewed' : 'No saved extraction'}
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          className="admin-icon-button"
                          aria-label={`Inspect ${b.title}`}
                          onClick={() => onBill(b.id)}
                        >
                          <ArrowRight size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty
              title="No receipts in this view"
              detail="Try a different filter or date range."
            />
          )}
          <Pagination
            total={data.total}
            offset={offset}
            next={data.nextOffset}
            onPage={setOffset}
          />
        </>
      )}
    </section>
  );
}
type Attempt = {
  id: string;
  billId: string;
  createdAt: string;
  model: string;
  estimatedCostUsd?: number;
  parsedModelOutput?: any;
  rawModelOutput?: string;
  prompt?: string;
  processedImages?: {
    auditImageUrl?: string;
    sourceUrl?: string;
  }[];
  adminReview?: Review;
  reviewRevision?: number;
};
function actorLabel(actor: ReceiptDetail['timeline'][number]['actor']): string | null {
  if (typeof actor === 'string') return actor;
  if (!actor || typeof actor !== 'object') return null;
  for (const value of [actor.name, actor.clientName, actor.userId, actor.kind]) {
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}
type ReceiptDetail = {
  timeline: {
    id: string;
    type: string;
    createdAt: string;
    actor?: string | { name?: string; kind?: string; userId?: string; clientName?: string };
    detail?: string;
    error?: string;
  }[];
  bill: any;
  records: Attempt[];
  warnings: string[];
};
const safeImage = (value: unknown): value is string =>
  typeof value === 'string' && /^https?:\/\//.test(value);
function ReceiptImage({ src, index }: { src: string; index: number }) {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <Notice>Receipt image {index + 1} is unavailable.</Notice>
  ) : (
    <a href={src} target="_blank" rel="noreferrer">
      <img
        src={src}
        alt={`Original receipt, page ${index + 1}. Open full size.`}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    </a>
  );
}
export function BillInspector({
  id,
  onClose,
  onUser,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onUser: (id: string) => void;
  onChanged: () => void;
}) {
  const [refresh, setRefresh] = useState(0),
    [attemptId, setAttemptId] = useState('');
  const { data, loading, error } = useReport<ReceiptDetail>(
    `/receipts/${encodeURIComponent(id)}`,
    {},
    refresh
  );
  const bill = data?.bill,
    attempt = data?.records.find((a) => a.id === attemptId) || data?.records[0];
  const attemptImages = (
    attempt?.processedImages?.map((i) => i.auditImageUrl || i.sourceUrl) || []
  ).filter(safeImage);
  const images = attemptImages.length
    ? attemptImages
    : [...(bill?.receiptImageUrls || []), bill?.receiptImageUrl].filter(safeImage);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="vs-design admin-ui admin-dialog admin-bill-dialog">
        <DialogHeader>
          <DialogTitle>{bill?.title || 'Bill investigation'}</DialogTitle>
          <DialogDescription>
            Inspect the current bill and its original receipt extraction.
          </DialogDescription>
        </DialogHeader>
        <LoadState loading={loading} error={error} retry={() => setRefresh((n) => n + 1)} />
        {data && bill && (
          <div className="admin-stack">
            <div className="admin-section-heading">
              <div className="admin-actions">
                <Status value={bill.status} />
                {bill.processingStatus && <Status value={bill.processingStatus} />}
                <span className="admin-muted">{dateTime(bill.createdAt || bill.date)}</span>
              </div>
              <div className="admin-actions">
                {typeof bill.shareId === 'string' && bill.shareId && !bill.isDeleted && !bill.shareRevokedAt ? (
                  <Button asChild variant="outline" size="lg">
                    <a href={`/b/${encodeURIComponent(bill.shareId)}?app=1`} target="_blank" rel="noopener noreferrer">
                      Open public bill <ExternalLink size={16} aria-hidden="true" />
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  </Button>
                ) : <span className="admin-muted">Public link unavailable</span>}
                <button className="admin-text-link" onClick={() => onUser(bill.userId)}>
                  Investigate creator <ArrowRight size={16} />
                </button>
              </div>
            </div>
            {bill.processingError && (
              <Notice danger>
                <strong>Processing error</strong>
                <p>
                  {typeof bill.processingError === 'string'
                    ? bill.processingError
                    : JSON.stringify(bill.processingError)}
                </p>
              </Notice>
            )}
            {data.warnings.map((w) => (
              <Notice key={w}>{w}</Notice>
            ))}
            {data.records.length > 0 && (
              <label className="admin-inline-label">
                Extraction attempt
                <select
                  aria-label="Extraction attempt"
                  value={attempt?.id}
                  onChange={(e) => setAttemptId(e.target.value)}
                >
                  {data.records.map((a, i) => (
                    <option key={a.id} value={a.id}>
                      {i === 0 ? 'Latest · ' : ''}
                      {dateTime(a.createdAt)} · {a.model}
                    </option>
                  ))}
                </select>
                <span className="admin-muted">Latest 10 attempts</span>
              </label>
            )}
            <div className="admin-receipt-grid">
              <section className="admin-receipt-image">
                <h3>
                  {attempt && !attemptImages.length ? 'Current bill receipt' : 'Original receipt'}
                </h3>
                {attempt && !attemptImages.length && (
                  <p className="admin-muted">
                    This attempt has no saved image. Confirm the current receipt matches before
                    reviewing it.
                  </p>
                )}
                {images.length ? (
                  images.map((src: string, index: number) => (
                    <ReceiptImage key={src} src={src} index={index} />
                  ))
                ) : (
                  <div className="admin-empty">
                    <FileImage size={28} />
                    <p>No receipt image available.</p>
                  </div>
                )}
              </section>
              <div className="admin-stack">
                {attempt?.parsedModelOutput ? (
                  <section className="admin-panel">
                    <h3>Original extraction</h3>
                    <p className="admin-muted">
                      {attempt.model} ·{' '}
                      {typeof attempt.estimatedCostUsd === 'number'
                        ? `$${attempt.estimatedCostUsd.toFixed(4)} estimated cost`
                        : 'Cost not recorded'}
                    </p>
                    <ItemList
                      items={attempt.parsedModelOutput.items || []}
                      currency={
                        attempt.parsedModelOutput.metadata?.currency || bill.currency || 'USD'
                      }
                    />
                    <ChargeBreakdown
                      value={attempt.parsedModelOutput.metadata || attempt.parsedModelOutput}
                      currency={
                        attempt.parsedModelOutput.metadata?.currency || bill.currency || 'USD'
                      }
                    />
                    <dl className="admin-profile-facts">
                      <div>
                        <dt>Extracted total</dt>
                        <dd>
                          {amount(
                            attempt.parsedModelOutput.metadata?.total == null &&
                              attempt.parsedModelOutput.total == null
                              ? null
                              : Number(
                                  attempt.parsedModelOutput.metadata?.total ??
                                    attempt.parsedModelOutput.total
                                ),
                            attempt.parsedModelOutput.metadata?.currency || bill.currency || 'USD'
                          )}
                        </dd>
                      </div>
                    </dl>
                    <details>
                      <summary>Full extraction</summary>
                      <pre>{JSON.stringify(attempt.parsedModelOutput, null, 2)}</pre>
                    </details>
                  </section>
                ) : (
                  <Notice>
                    No parsed extraction is available for this attempt. Failed processing is tracked
                    separately from reviewed accuracy.
                  </Notice>
                )}
                {attempt && (
                  <ReceiptReview
                    key={attempt.id + ':' + (attempt.reviewRevision || 0)}
                    attempt={attempt}
                    onSaved={() => {
                      setRefresh((n) => n + 1);
                      onChanged();
                    }}
                  />
                )}
                <section className="admin-panel">
                  <h3>Current bill</h3>
                  <p className="admin-muted">May include edits made after extraction.</p>
                  <ItemList items={bill.items || []} currency={bill.currency || 'USD'} />
                  <ChargeBreakdown value={bill} currency={bill.currency || 'USD'} />
                  <div className="admin-fact-line">
                    <strong>Total</strong>
                    <strong>
                      {bill.total == null
                        ? 'Not recorded'
                        : amount(Number(bill.total), bill.currency || 'USD')}
                    </strong>
                  </div>
                  {bill.participantShares?.map((share: any) => (
                    <div className="admin-fact-line" key={share.participantId}>
                      <span>
                        {bill.participants?.find((p: any) => p.id === share.participantId)?.name ||
                          share.participantName ||
                          share.participantId}
                        <small>{share.paymentStatus || 'Unpaid'}</small>
                      </span>
                      <strong>{amount(Number(share.total), bill.currency || 'USD')}</strong>
                    </div>
                  ))}
                </section>
              </div>
            </div>
            {data.timeline?.length > 0 && (
              <details>
                <summary>Recorded bill activity ({data.timeline.length} most recent)</summary>
                <ol className="admin-timeline">
                  {data.timeline.map((event, index) => (
                    <li key={`${event.id}-${index}`}>
                      <span className="admin-timeline-dot" />
                      <div>
                        <strong>{humanize(event.type)}</strong>
                        {actorLabel(event.actor) && <small>{actorLabel(event.actor)}</small>}
                        {event.detail && <p>{event.detail}</p>}
                        {event.error && <p className="admin-danger-text">{event.error}</p>}
                      </div>
                      <time>{dateTime(event.createdAt)}</time>
                    </li>
                  ))}
                </ol>
              </details>
            )}
            <details>
              <summary>Bill ID & saved processing details</summary>
              <code>{bill.id}</code>
              {attempt && (
                <>
                  <h4>Raw model response</h4>
                  <pre>{attempt.rawModelOutput || 'Not saved'}</pre>
                  <h4>Processing prompt</h4>
                  <pre>{attempt.prompt || 'Not saved'}</pre>
                </>
              )}
            </details>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
function ChargeBreakdown({ value, currency }: { value: any; currency: string }) {
  const entries: Array<[string, unknown]> = [
    ['Subtotal', value.subtotal],
    ['Tax', value.tax],
    ['Tip', value.tip],
    ...Object.entries(value.discounts || {}).map(
      ([name, total]) => [`Discount · ${name}`, total] as [string, unknown]
    ),
    ...Object.entries(value.surcharges || {}).map(
      ([name, total]) => [name, total] as [string, unknown]
    ),
  ];
  return (
    <div>
      {entries
        .filter(([, total]) => total != null && Number.isFinite(Number(total)))
        .map(([label, total]) => (
          <div className="admin-fact-line" key={label}>
            <span>{label}</span>
            <strong>{amount(Number(total), currency)}</strong>
          </div>
        ))}
    </div>
  );
}
function ItemList({ items, currency }: { items: any[]; currency: string }) {
  return Array.isArray(items) && items.length ? (
    <ul className="admin-items">
      {items.map((item, index) => (
        <li key={item.id || index}>
          <span>
            {item.name || item.description || 'Unnamed item'}
            <small>
              Quantity {item.quantity || 1} · Line price{' '}
              {amount(Number(item.price ?? item.unit_price ?? 0), currency)}
            </small>
            {item.addons?.map((addon: any, i: number) => (
              <small key={i}>
                + {addon.name}: {amount(Number(addon.price), currency)}
                {addon.included_in_price ? ' (included)' : ''}
              </small>
            ))}
          </span>
          <strong>
            {amount(
              Number(
                item.amount ??
                  item.total ??
                  Number(item.price || 0) +
                    (Array.isArray(item.addons) ? item.addons : []).reduce(
                      (sum: number, addon: any) =>
                        sum + (addon.included_in_price ? 0 : Number(addon.price || 0)),
                      0
                    )
              ),
              currency
            )}
          </strong>
        </li>
      ))}
    </ul>
  ) : (
    <p className="admin-muted">No items recorded.</p>
  );
}
function ReceiptReview({ attempt, onSaved }: { attempt: Attempt; onSaved: () => void }) {
  const [outcome, setOutcome] = useState(attempt.adminReview?.outcome || 'accurate'),
    [note, setNote] = useState(attempt.adminReview?.note || ''),
    [saving, setSaving] = useState(false),
    [error, setError] = useState('');
  const gradeable =
    Array.isArray(attempt.parsedModelOutput?.items) &&
    attempt.parsedModelOutput?.financial_validation?.status !== 'failed';
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await api.put(`${endpoint}/receipt-reviews/${encodeURIComponent(attempt.billId)}`, {
        attemptId: attempt.id,
        outcome,
        note,
        expectedRevision: attempt.reviewRevision || 0,
      });
      onSaved();
    } catch (e) {
      setError(message(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form className="admin-panel admin-review-form" onSubmit={submit}>
      <h3>Quality review</h3>
      <p className="admin-muted">
        Compare the original extraction with the receipt: items, quantities, tax, tip, currency, and
        total. Saving a review does not change the bill.
      </p>
      {attempt.adminReview && (
        <p className="admin-review-saved">
          <Status value={attempt.adminReview.outcome} />
          <span>Saved {dateTime(attempt.adminReview.reviewedAt)}</span>
        </p>
      )}
      {gradeable ? (
        <>
          <fieldset>
            <legend className="sr-only">Review outcome</legend>
            <label>
              <input
                type="radio"
                name={`outcome-${attempt.id}`}
                checked={outcome === 'accurate'}
                onChange={() => setOutcome('accurate')}
              />
              Accurate
            </label>
            <label>
              <input
                type="radio"
                name={`outcome-${attempt.id}`}
                checked={outcome === 'needs_correction'}
                onChange={() => setOutcome('needs_correction')}
              />
              Needs correction
            </label>
          </fieldset>
          <label>
            Review notes {outcome === 'accurate' ? '(optional)' : '(required)'}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
              minLength={outcome === 'needs_correction' ? 5 : undefined}
              required={outcome === 'needs_correction'}
              placeholder="What matched, or what needs correcting?"
            />
          </label>
          {error && (
            <Notice danger>
              <p>{error}</p>
              <Button type="button" variant="outline" onClick={onSaved}>
                Reload review
              </Button>
            </Notice>
          )}
          <Button
            type="submit"
            disabled={saving || (outcome === 'needs_correction' && note.trim().length < 5)}
          >
            {saving ? 'Saving…' : attempt.adminReview ? 'Update review' : 'Save review'}
          </Button>
        </>
      ) : (
        <p className="admin-muted">
          This attempt cannot be graded because parsing did not complete.
        </p>
      )}
    </form>
  );
}
